import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useMutation } from 'react-query'
import { useNavigate } from 'react-router-dom'
import JumboCardQuick from '@jumbo/components/JumboCardQuick'
import { Grid } from '@mui/material'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded'
import { getEnrolmentList } from 'app/redux/store/features/enrolments'
import EnrolmentGridFilter from './components/EnrolmentGridFilter'
import EnrolmentGridData from './components/EnrolmentGridData'
import { Capitalize } from 'app/utils/components/StringUtils/stringUtils'
import { useSnackbar } from 'notistack'

const EnrolmentsGrid = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { enqueueSnackbar } = useSnackbar()
    const [filter, setFilter] = useState({
        firstName: '',
        lastName: '',
        familyName: '',
        email: '',
        sort: 'CreateTime',
        isDescending: true,
    })
    const { loading, error } = useSelector((state) => state.enrolments)
    const [apiCallInProgress, setApiCallInProgress] = useState(false)
    const [currentPage, setCurrentPage] = useState(0)
    const pageSize = 25

    useEffect(() => {
        if (!apiCallInProgress || loading) return
        setApiCallInProgress(false)

        if (error) handleError(error)
    }, [loading])

    useEffect(() => {
        setApiCallInProgress(true)
        dispatch(getEnrolmentList({ ...filter, skip: 0, take: pageSize }))
    }, [dispatch])

    function handleError(error) {
        if (error.status === '401') {
            navigate('/profile/signout')
        } else if (error.detail) {
            enqueueSnackbar(error.detail, {
                variant: 'error',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                },
            })
        } else {
            enqueueSnackbar(error, {
                variant: 'error',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                },
            })
        }
    }

    function init(initialValue) {
        return {
            gridFilterVisible: initialValue.gridFilterVisible,
            filterIconColor: initialValue.filterIconColor,
        }
    }

    function userReducer(state, action) {
        switch (action.type) {
            case 'set-grid-filter':
                return {
                    ...state,
                    gridFilterVisible: action.payload,
                }
            case 'set-filter-icon-color':
                return {
                    ...state,
                    filterIconColor: action.payload,
                }
            default:
                return state
        }
    }

    const [enrolmentGrid, setEnrolmentGrid] = React.useReducer(
        userReducer,
        {
            gridFilterVisible: false,
            filterIconColor: false,
        },
        init
    )

    const applyFilter = useMutation({
        mutationFn: (currentfilter) => {
            dispatch(getEnrolmentList({ ...currentfilter, skip: 0, take: pageSize }, true))
            setFilter(currentfilter)
        },
    })

    const onPageChange = (pageNumber) => {
        const currentFilter = { ...filter, skip: pageSize * pageNumber, take: pageSize }
        dispatch(getEnrolmentList(currentFilter))
        setFilter(currentFilter)
    }

    const onColumnSort = (sortOrder) => {
        const columnName = Capitalize(sortOrder?.[0]?.columnName || '')
        const currentFilter = {
            ...filter,
            sort: columnName,
            isDescending: sortOrder[0].direction !== 'asc',
            take: pageSize,
        }
        dispatch(getEnrolmentList(currentFilter))
        setFilter(currentFilter)
    }

    const onRefresh = () => {
        setApiCallInProgress(true)
        dispatch(getEnrolmentList({ ...filter, skip: 0, take: pageSize }, true))
        setCurrentPage(0)
    }

    const setGridFilter = React.useCallback(
        (value) => {
            setEnrolmentGrid({ type: 'set-grid-filter', payload: value })
        },
        [setEnrolmentGrid]
    )

    // const onAddEnrolment = () => {                       //Todo: We will add this again in future
    //     navigate('/registrations/enrolments/a dd')
    // }

    const setFilterIconColor = React.useCallback(
        (value) => {
            setEnrolmentGrid({ type: 'set-filter-icon-color', payload: value })
        },
        [setEnrolmentGrid]
    )

    return (
        <JumboCardQuick
            noWrapper
            title={
                <Typography color={'common.white'} variant={'h3'}>
                    <AutorenewRoundedIcon
                        onClick={onRefresh}
                        fontSize={'large'}
                        sx={{
                            mb: -1.2,
                            ml: -2,
                            mr: 0.5,
                            '&:hover': {
                                cursor: 'pointer',
                            },
                        }}
                    />
                    Enrolment List
                </Typography>
            }
            sx={{ bgcolor: 'primary.main' }}
            headerSx={{
                height: 40,
            }}
            action={
                <Stack direction={'row'} spacing={4} sx={{ mt: -2 }}>
                    {/* <AddIcon                      //Todo: We will add this again in future
                        fontSize={'large'}
                        variant={'contained'}
                        sx={{
                            pt: 0,
                            pb: 0,
                            color: 'common.white',
                            '&:hover': {
                                cursor: 'pointer',
                            },
                        }}
                        onClick={onAddEnrolment}
                    /> */}
                    <FilterAltIcon
                        fontSize='large'
                        sx={{
                            mt: -1.8,
                            color:
                                enrolmentGrid.filterIconColor && !enrolmentGrid.gridFilterVisible
                                    ? 'success.main'
                                    : 'common.white',
                            '&:hover': {
                                cursor: 'pointer',
                            },
                        }}
                        onClick={() => setGridFilter(!enrolmentGrid.gridFilterVisible)}
                    />
                </Stack>
            }>
            <Grid>
                <EnrolmentGridFilter
                    mutation={applyFilter}
                    display={enrolmentGrid.gridFilterVisible ? '' : 'none'}
                    filterIconColor={(data) => setFilterIconColor(data)}
                />
            </Grid>
            <Grid>
                <EnrolmentGridData
                    onRefresh={onRefresh}
                    onPageChange={onPageChange}
                    onColumnSort={onColumnSort}
                    setCurrentPage={setCurrentPage}
                    currentPage={currentPage}
                    handleError={handleError}
                />
            </Grid>
        </JumboCardQuick>
    )
}
export default EnrolmentsGrid
